import { addDays } from 'date-fns/addDays'
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfWeek } from 'date-fns/endOfWeek'
import { format } from 'date-fns/format'
import { isToday } from 'date-fns/isToday'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfWeek } from 'date-fns/startOfWeek'
import { useCallback, useMemo, useState } from 'react'
import { Eyebrow } from 'components/Eyebrow'
import { Text } from 'components/Text'
import type { CalendarEntry, CalendarView } from '../types'
import { CalendarDayCell } from './CalendarDayCell'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Teaching state: on a zero-entry calendar the hover-only "+ Add post" hints
// are undiscoverable (nothing invites the pointer in), so this many upcoming
// day cells (today onward) show the hint at rest. Capped so an empty month
// reads as an invitation, not a wall of 30 identical hints.
const TEACHING_HINT_DAYS = 5

interface ContentCalendarProps {
  currentDate: Date
  view: CalendarView
  entries: CalendarEntry[]
  selectedEntryId?: string
  /** Opens PostModal for an entry (week cards + month bars + popover cards). */
  onOpenPost?: (entry: CalendarEntry) => void
  /** creativeId currently loading its PostModal payload — drives a per-card veil. */
  openingId?: string | null
  draggingEntry?: CalendarEntry | null
  /** When provided, day tap opens external handler instead of inline popover */
  onDayTap?: (dateStr: string) => void
  /** True when the visible range has zero entries — flips the teaching state
   *  (the next few upcoming day cells show "+ Add post" at rest). */
  calendarEmpty?: boolean
}

export function ContentCalendar({
  currentDate,
  view,
  entries,
  selectedEntryId,
  onOpenPost,
  openingId,
  draggingEntry,
  onDayTap,
  calendarEmpty = false,
}: ContentCalendarProps) {
  const days = useMemo(() => {
    if (view === 'week') {
      const weekStart = startOfWeek(currentDate)
      return eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })
    }
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calStart = startOfWeek(monthStart)
    const calEnd = endOfWeek(monthEnd)
    return eachDayOfInterval({ start: calStart, end: calEnd })
  }, [currentDate, view])

  // Group entries by date for O(1) lookup
  const entriesByDate = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>()
    for (const entry of entries) {
      const existing = map.get(entry.date) ?? []
      existing.push(entry)
      map.set(entry.date, existing)
    }
    return map
  }, [entries])

  const isCompact = view === 'month'
  const rowCount = Math.ceil(days.length / 7)

  // Dates whose empty cell renders its "+ Add post" hint at rest (see
  // TEACHING_HINT_DAYS). Only computed for the zero-entry teaching state.
  const teachingDates = useMemo(() => {
    if (!calendarEmpty) return null
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    const upcoming: string[] = []
    for (const day of days) {
      const dateStr = format(day, 'yyyy-MM-dd')
      if (dateStr >= todayStr) upcoming.push(dateStr)
      if (upcoming.length === TEACHING_HINT_DAYS) break
    }
    return new Set(upcoming)
  }, [calendarEmpty, days])

  // Lifted popover state: only one cell popover open at a time
  const [openPopoverDate, setOpenPopoverDate] = useState<string | null>(null)
  const handlePopoverToggle = useCallback((dateStr: string) => {
    setOpenPopoverDate(prev => (prev === dateStr ? null : dateStr))
  }, [])
  const handlePopoverClose = useCallback((dateStr: string) => {
    setOpenPopoverDate(prev => (prev === dateStr ? null : prev))
  }, [])

  // Row sizing:
  //   Month — each row is at least 160 px so the page can scroll naturally.
  //   Week  — single row fills at least the visible viewport height below the
  //           sticky headers so week-view cells get a definite height
  //           (needed because the cells themselves use h-full).
  const rowTemplate =
    view === 'month'
      ? `repeat(${rowCount}, minmax(160px, auto))`
      : `repeat(${rowCount}, minmax(calc(100svh - 200px), auto))`

  return (
    <div className="flex flex-1 flex-col">
      {/* ── Sticky column headers ──────────────────────────────────────────────
          Week view: day name + date badge per column (combined, taller row).
          Month view: just the day name abbreviations. */}
      {view === 'week' ? (
        <div className="sticky top-14 z-20 bg-bg-2 shadow-[0_4px_8px_-6px_rgba(0,0,0,0.5)]">
          <div className="grid grid-cols-7 border-b border-line">
            {days.map((day, i) => (
              <div
                key={`wkd-${i}`}
                className="border-r border-line px-2 py-2 text-center last:border-r-0"
              >
                <Eyebrow
                  tone={isToday(day) ? 'default' : 'quiet'}
                  className={isToday(day) ? '!text-acid' : ''}
                >
                  {WEEKDAY_LABELS[i]}
                </Eyebrow>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 border-b border-line">
            {days.map((day, i) => (
              <div
                key={`date-${i}`}
                className="border-r border-line px-3 py-2 text-center last:border-r-0"
              >
                <Text
                  as="p"
                  variant="body-sm"
                  weight="medium"
                  tone={isToday(day) ? 'acid' : 'paper'}
                >
                  {format(day, 'MMM d')}
                </Text>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="sticky top-14 z-20 grid grid-cols-7 border-b bg-bg-2 shadow-[0_4px_8px_-6px_rgba(0,0,0,0.5)]">
          {WEEKDAY_LABELS.map(day => (
            <div key={day} className="border-r px-2 py-2 text-center last:border-r-0">
              <Eyebrow tone="quiet">{day}</Eyebrow>
            </div>
          ))}
        </div>
      )}

      {/* Day grid — parent page scrolls */}
      <div className="grid flex-1 grid-cols-7" style={{ gridTemplateRows: rowTemplate }}>
        {days.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd')
          return (
            <CalendarDayCell
              key={dateStr}
              date={date}
              entries={entriesByDate.get(dateStr) ?? []}
              currentMonth={currentDate}
              compact={isCompact}
              selectedEntryId={selectedEntryId}
              onOpenPost={onOpenPost}
              openingId={openingId}
              popoverOpen={!onDayTap && openPopoverDate === dateStr}
              onPopoverToggle={onDayTap ? undefined : handlePopoverToggle}
              onPopoverClose={onDayTap ? undefined : handlePopoverClose}
              draggingEntry={draggingEntry}
              onDayTap={onDayTap}
              hintAtRest={teachingDates?.has(dateStr) ?? false}
            />
          )
        })}
      </div>
    </div>
  )
}
